import {
  ButtonFinal,
  CallDataPedagogy,
  GroupRequestCard,
  HangUpPopup,
  LayoutType,
  PedagogyActionOrigin,
  PedagogyRoomUserList,
  PedagogyRoomUserListTab,
  PedagogySOSOverlay,
  PedagogyUtils,
  PedagoSupervisorHeader,
  PedagoSupervisorTabs,
  PedagoSupervisorTabsContentHeader,
  Popup,
  ReactIcons,
  ScreenPopup,
  UserCallData,
  UserRequestCard,
} from "@kalyzee/escaet-ui";
import { isMeetingSessionRoleGreaterThanOrEqual, isSuperUser, MeetingSessionOvenMediaReceiver } from "@kalyzee/kast-web-sdk";
import { MeetingSessionRole } from "@kalyzee/kast-web-sdk/http";
import { delay, Overlay, useRefAutoUpdate } from "@kalyzee/kast-web-sdk/web-components";
import { MeetingCallState, MeetingRoomStatus, MeetingSessionHighlightingState, MeetingState, MeetingUserData, User } from "@kalyzee/kast-web-sdk/websocket";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import DashboardChat from "../../../../../components/dashboard/Chat";
import { useGroupsHighlightRequestsList, useUsersHighlightRequestsList } from "../../../../../components/dashboard/HighlightRequestsList";
import { PageMasterMode, usePageContext } from "../../../../../components/navigation/PageContext";
import { PedagogyExpertDashboardContainer } from "../../../../../components/pedagogy/containers/expertDashboardContainer";
import { PedagogyGroupContainer } from "../../../../../components/pedagogy/containers/groupContainer";
import { PedagogyHighlightContainer } from "../../../../../components/pedagogy/containers/highlightContainer";
import {
  PedagogyStudentDashboardContainer,
  PedagogyStudentDashboardContainerRef,
} from "../../../../../components/pedagogy/containers/studentDashboardContainer";
import { PedagogySurveyContainer } from "../../../../../components/pedagogy/containers/surveyContainer";
import { PedagogyMessageForm } from "../../../../../components/pedagogy/MessageForm";
import { RoomViewer } from "../../../../../components/pedagogy/RoomViewer";
import MeetingPlayerStyleContainer from "../../../../../components/player/container/MeetingPlayerStyleContainer";
import MeetingSessionPlayer from "../../../../../components/player/MeetingSessionPlayer";
import PauseView from "../../../../../components/utils/PauseView";
import { RightLeftArrows } from "../../../../../components/utils/RightLeftArrows";
import { getExpertAudioValidator } from "../../../../../helpers/audio";
import { getEscaetUserCallData, startSessionHighlight, updateUserData } from "../../../../../helpers/meeting";
import { addPopup, removePopup, removePopupByGroup, removePopupByTag } from "../../../../../helpers/popup";
import { toastError } from "../../../../../helpers/toast";
import { useMeetingContainerAPI, useMeetingSelector, useMeetingStateUpdater } from "../../../../../hooks/meeting";
import { usePedagogyNotifications, usePedagogyUsers } from "../../../../../hooks/pedagogy";
import {
  getCurrentExpertGroupView,
  getExpertView,
  useActivityData,
  useExpertAvailableViews,
  usePopulatedMeetingData,
} from "../../../../../hooks/populatedData";
import { EscaetPedagogyAction, MeetingPedagogyUserData } from "../../../../../interfaces/pedagogy";
import { useUser } from "../../../../../store/session/hooks";
import { SessionMode } from "../../../../../store/session/slices";
import { usePedagogyContext } from "../../context";
import { MeetingPedagogyInterventionGroupPageQueryAction } from "../intervention/content/group";
import styles from "./dashboard.module.css";

export const MeetingDashboardPedagogyPage = () => {
  const pageContext = usePageContext<SessionMode.MASTER>();
  const pageContextRef = useRefAutoUpdate(pageContext);
  const selfUser = useUser();
  const pedagogyCtx = usePedagogyContext();
  const pedagogyCtxRef = useRefAutoUpdate(pedagogyCtx);

  const [userPopupData, setUserPopupData] = useState<{
    id: string;
    mode: "user" | "session";
    sessionIndex: number;
  }>();

  const [displaySearchUserPopup, setDisplaySearchUserPopup] = useState<boolean>(false);
  const meetingContainerAPI = useMeetingContainerAPI(pageContextRef.current?.params?.id);
  const [meeting, meetingRef] = useMeetingSelector(meetingContainerAPI, (state) => state.meeting);
  const [_, sessionRef] = useMeetingSelector(meetingContainerAPI, (state) => state.session);
  const [receptionSessions] = useMeetingSelector(meetingContainerAPI, (state) => state.receptionSessions);

  const [audioEnabled, setAudioEnabled] = useState(true);

  const [tabIndex, setTabIndex] = useState(0);

  // peda users
  const pedaUsers = usePedagogyUsers();

  usePedagogyNotifications(meetingContainerAPI);

  // call users
  const usersCallData = useMemo(() => {
    if (!meeting) return [];
    const callData = getEscaetUserCallData(meeting);
    return callData.map((d) => {
      const peda = pedaUsers.find((u) => u.id === d.id);
      if (!peda?.withPedagogy) return d;
      return {
        ...d,
        withPedagogy: true,
      };
    });
  }, [meeting, pedaUsers]);

  const usersInWaitingRoom = useMemo(() => usersCallData.filter((u) => u.inWaitingRoom), [usersCallData]);
  const usersStats = useMemo(() => {
    const result = {
      present: 0,
      distance: 0,
      total: 0,
    };
    usersCallData.forEach((u) => {
      if (!u.status) return;
      if (u.status === "on_site") {
        result.present++;
        result.total++;
      } else if (u.status === "missing") {
        result.total++;
      } else if (u.status === "online") {
        result.distance++;
        result.total++;
      }
    });
    return result;
  }, [usersCallData]);

  const [layoutType, setLayoutType] = useState(LayoutType.Square);

  const studentTabRef = useRef<PedagogyStudentDashboardContainerRef>(null);

  const populatedMeetingData = usePopulatedMeetingData(meetingContainerAPI);
  const activityData = useActivityData(populatedMeetingData);
  const availableExpertViews = useExpertAvailableViews(populatedMeetingData, activityData, pedagogyCtx?.spiedGroupId);
  const currentExpertView = availableExpertViews[0];
  const highlightView = useMemo(() => availableExpertViews?.find((v) => v.type === "highlight"), [availableExpertViews]);
  const userDataInSosMode = useMemo(() => {
    const onlineExpertSessions = meeting?.sessions?.filter((s) => isMeetingSessionRoleGreaterThanOrEqual(s.role, MeetingSessionRole.MODERATOR)) ?? [];
    const userData: MeetingUserData[] = [];
    onlineExpertSessions.forEach((s) => {
      const u = meeting?.usersData?.find((u) => u.userId === s.user?.id && u.sosMode);
      if (u) userData.push(u);
    });
    return userData;
  }, [meeting]);
  const sos = userDataInSosMode.length > 0;
  const [displayPopupSOS, setDisplayPopupSOS] = useState(sos);

  const usersHighlightRequestsList = useUsersHighlightRequestsList(meetingContainerAPI);
  const groupsHighlightRequestsList = useGroupsHighlightRequestsList(meetingContainerAPI);

  const { ongoingGroups } = useMemo(() => {
    const ongoing = populatedMeetingData?.rooms.groups.filter((group) => group.room.status === MeetingRoomStatus.Ongoing) ?? [];
    return { ongoingGroups: ongoing };
  }, [populatedMeetingData]);

  const pedaMeetingId = pedagogyCtx?.pedagogyMeetings[0]?.id;

  const [inWaitingRoom, setInWaitingRoom] = useState(false);

  const { update: updateMeetingState } = useMeetingStateUpdater(meetingContainerAPI);

  const [pedagogyRoomUserListTab, setPedagogyRoomUserListTab] = useState<PedagogyRoomUserListTab>("behaviors");
  const pedagogyRoomUserListActions = useMemo(
    () =>
      pedagogyCtx?.actions.map((a) => {
        let newName: string | undefined = undefined;
        if (a.type === "tolerate") newName = "Tolérer et retirer de la liste";
        // if (a.type === "warn") newName = pedagogyRoomUserListTab === "behaviors" ? "Avertir" : a.name;
        return newName ? { ...a, name: newName } : a;
      }) ?? [],
    [pedagogyCtx, pedagogyRoomUserListTab]
  );

  const roomViewerPedagogyActions = useMemo(
    () =>
      pedagogyCtx?.actions.map((a) => {
        let newName: string | undefined = undefined;
        if (a.type === "exclude") newName = "Refuser l'entrée en cours";
        if (a.type === "contact") newName = "Entrer en communication";
        return newName ? { ...a, name: newName } : a;
      }) ?? [],
    [pedagogyCtx]
  );

  const connectedStudentSessions = useMemo(() => {
    return pedagogyCtx?.orderedSessions?.filter((s) => s.connected) ?? [];
  }, [pedagogyCtx?.orderedSessions]);

  // Survey
  const [currentSurvey] = useMeetingSelector(meetingContainerAPI, (state) => (state.surveys?.length ? state.surveys[state.surveys.length - 1] : undefined));

  // Audios
  const views = useMemo(() => {
    return {
      normal: getExpertView("normal", populatedMeetingData, activityData, pedagogyCtx?.spiedGroupId),
      highlight: getExpertView("highlight", populatedMeetingData, activityData, pedagogyCtx?.spiedGroupId),
      group: getCurrentExpertGroupView(populatedMeetingData, activityData, pedagogyCtx?.spiedGroupId),
    };
  }, [populatedMeetingData, activityData, pedagogyCtx?.spiedGroupId]);

  const audioValidator = useCallback(
    (tab: "student" | "expert" | "highlight" | "group" | "survey") => {
      let audioValidator: (session: MeetingSessionOvenMediaReceiver) => boolean = getExpertAudioValidator(undefined);
      const groups = views.group ? true : false;
      // console.log("View : ", views, views.normal);
      if (groups) {
        if (tab === "student") {
          // only student
          audioValidator = (session) => {
            const sessionId = session.data?.session?.id;
            const data = views.normal?.experts.find((data) => data.session.id === sessionId);
            if (data) return data.audio;
            return false;
          };
        } else if (tab === "expert") {
          // onyly experts
          audioValidator = (session) => {
            const sessionId = session.data?.session?.id;
            const data = views.normal?.students.find((data) => data.session.id === sessionId);
            if (data) return data.audio;
            return false;
          };
        } else if (tab === "highlight") audioValidator = getExpertAudioValidator(views.highlight);
        else if (tab === "group") audioValidator = getExpertAudioValidator(views.group);
        else if (tab === "survey") audioValidator = getExpertAudioValidator(views.group);
      } else {
        if (tab === "student") audioValidator = getExpertAudioValidator(views.normal);
        else if (tab === "expert") audioValidator = getExpertAudioValidator(views.normal);
        else if (tab === "highlight") audioValidator = getExpertAudioValidator(views.highlight);
        else if (tab === "group") audioValidator = getExpertAudioValidator(views.group);
        else if (tab === "survey") audioValidator = getExpertAudioValidator(views.normal);
      }

      return audioValidator;
    },
    [views]
  );

  const studentAudioValidator = useCallback((session: MeetingSessionOvenMediaReceiver) => audioValidator("student")(session), [audioValidator]);
  const expertAudioValidator = useCallback((session: MeetingSessionOvenMediaReceiver) => audioValidator("expert")(session), [audioValidator]);
  const highlightAudioValidator = useCallback((session: MeetingSessionOvenMediaReceiver) => audioValidator("highlight")(session), [audioValidator]);
  const groupAudioValidator = useCallback((session: MeetingSessionOvenMediaReceiver) => audioValidator("group")(session), [audioValidator]);
  const surveyAudioValidator = useCallback((session: MeetingSessionOvenMediaReceiver) => audioValidator("survey")(session), [audioValidator]);

  // ---------------------------------------------------------------------- //
  // ---------------------------   UTILS ----------------------------- //
  // ---------------------------------------------------------------------- //

  const stop = async (): Promise<boolean> => {
    pageContextRef.current?.setLoading(true);
    await delay(200);
    const result = await updateMeetingState(MeetingState.STOPPED);
    pageContextRef.current?.setLoading(false);
    if (result && !result.error && result.response?.meeting?.state === MeetingState.STOPPED) {
      pageContextRef.current?.goTo(PageMasterMode.DASHBOARD);
      return true;
    }
    toastError("Une erreur est apparue.");
    return false;
  };

  const seeUserProfile = (user: UserCallData) => {
    const userIsOnline = meeting?.sessions?.some((s) => s.user?.id === user.id);
    if (!userIsOnline) {
      toastError("L'utilisateur est introuvable.");
      return;
    }
    setDisplaySearchUserPopup(false);
    setUserPopupData({ id: user.id, mode: "user", sessionIndex: 0 });
  };

  const getSessions = (data: { id: string; mode: "user" | "session" }) => {
    if (data.mode === "user") {
      return meeting?.sessions?.filter((s) => s.user?.id === data.id) ?? [];
    } else {
      const session = meeting?.sessions?.find((s) => s.id === data.id);
      return session ? [session] : [];
    }
  };

  // ---------------------------------------------------------------------- //
  // ---------------------------   EFFECT ----------------------------- //
  // ---------------------------------------------------------------------- //

  useEffect(() => {
    if (!userPopupData) return;
    const sessions = getSessions(userPopupData);
    const pedaUserId = sessions[0]?.user?.id;
    if (!pedaUserId) return;
    const pedaUser = pedaUsers.find((u) => u.id === pedaUserId);
    if (pedaUser?.withPedagogy) {
      setUserPopupData(undefined);
    }
  }, [userPopupData, pedaUsers]);

  useEffect(() => {
    const validator = pedagogyCtxRef.current?.addAudioValidator(() => {
      return audioEnabled;
    });
    return () => {
      validator?.flush();
    };
  }, [audioEnabled]);

  useEffect(() => {
    setDisplayPopupSOS(sos);
  }, [sos]);

  useEffect(() => {
    return () => {
      removePopupByGroup("main");
    };
  }, []);

  // ---------------------------------------------------------------------- //
  // ---------------------------   RENDER ----------------------------- //
  // ---------------------------------------------------------------------- //

  const renderLeftPanel = () => {
    return (
      <div className={styles.left}>
        <PedagogyRoomUserList
          rooms={[]}
          users={pedaUsers}
          actions={pedagogyRoomUserListActions}
          enabledBadBehaviors={{
            hasNoCamera: true,
            hasBadNetwork: false,
            isNotAttentive: true,
          }}
          defaultTab={pedagogyRoomUserListTab}
          onTab={(tab: PedagogyRoomUserListTab) => setPedagogyRoomUserListTab(tab)}
        />
        <PedagogyUtils.ActionButtons
          inWaitingRoom={usersInWaitingRoom.length}
          onClick={(index: number) => {
            if (index === 0) {
              // premier bouton : Entrer dans la salle d'attente
              setInWaitingRoom(true);
            } else if (index === 1) {
              // deuxième bouton : Echanger avec l'expert
              pageContextRef.current?.goTo(PageMasterMode.MEETING_INTERVENTION_TALK_TO_EXPERT, pageContextRef.current.params);
            } else if (index === 2) {
              // troisième bouton : Intervenir en classe
              pageContextRef.current?.goTo(PageMasterMode.MEETING_INTERVENTION_DASHBOARD, pageContextRef.current.params);
            }
          }}
          container={{
            style: { width: "100%" },
            className: styles.actionButtons,
          }}
          sos={sos}
        />
        {meeting && (
          <div key={"buttons-container"} style={{ width: "100%", display: "flex", gap: 5, alignItems: "center" }}>
            <PedagogyUtils.HangUpButton
              style={{ flex: 1, minWidth: 0 }}
              onClick={() => {
                window.focus();
                removePopupByTag("meeting-hangup-popup");
                const start = meeting?.startedAt ? new Date(meeting?.startedAt) : undefined;
                const overlay = (
                  <HangUpPopup
                    startCourseDate={start}
                    onNext={async () => {
                      removePopup(overlay);
                      await stop();
                    }}
                    onClose={() => {
                      removePopup(overlay);
                    }}
                  />
                );
                addPopup(overlay, "meeting-hangup-popup", "main");
              }}
            />
            {meeting.state === MeetingState.PAUSED ? (
              <PedagogyUtils.PlayButton
                date={new Date(meeting?.pausedAt ?? new Date())}
                style={{ flex: 1, minWidth: 0 }}
                onClick={() => updateMeetingState(MeetingState.RUNNING)}
              />
            ) : (
              <PedagogyUtils.PauseButton
                date={new Date(meeting?.startedAt ?? new Date())}
                style={{ flex: 1, minWidth: 0 }}
                onClick={() => updateMeetingState(MeetingState.PAUSED)}
              />
            )}
          </div>
        )}
      </div>
    );
  };

  const renderContent = () => {
    return (
      <div className={styles.center}>
        <PedagoSupervisorTabs.Container
          className={styles.tabsContainer}
          selected={tabIndex}
          onSelect={(i: number) => {
            setTabIndex(i);
          }}
          contentHeader={
            <PedagoSupervisorTabsContentHeader
              interventions={{
                onClose: () => console.log("onClose"),
                title: "Demandes d'intervention",
                Icon: ReactIcons.FingerGrey,
                onAcceptAll: async () => {
                  if (ongoingGroups.length) await groupsHighlightRequestsList.respondAll(true);
                  else await usersHighlightRequestsList.respondAll(true);
                },
                onCancelAll: async () => {
                  if (ongoingGroups.length) await groupsHighlightRequestsList.respondAll(false);
                  else await usersHighlightRequestsList.respondAll(false);
                },
                cards: ongoingGroups.length
                  ? groupsHighlightRequestsList.groups.map((group, index) => (
                      <GroupRequestCard
                        key={index}
                        name={`Groupe ${group.room.name ?? ""}`}
                        onAccept={() => groupsHighlightRequestsList.respond(group.room, true)}
                        onCancel={() => groupsHighlightRequestsList.respond(group.room, false)}
                      />
                    ))
                  : usersHighlightRequestsList.users.map((user, index) => (
                      <UserRequestCard
                        key={index}
                        profilePicture={user.profilePicture}
                        firstName={user.firstName}
                        lastName={user.lastName}
                        onAccept={() => usersHighlightRequestsList.respond(user.session, true)}
                        onCancel={() => usersHighlightRequestsList.respond(user.session, false)}
                      />
                    )),
              }}
              disableManageStreamsButton={tabIndex !== 0}
              disableLayoutSelector={tabIndex !== 0 && tabIndex !== 2}
              disableListenSwitch={false}
              disableInterventions={false}
              style={{ width: "100%" }}
              listen={audioEnabled}
              onListen={(value: boolean) => setAudioEnabled(value)}
              layoutSelected={layoutType}
              onLayoutSeletor={(value: LayoutType) => setLayoutType(value)}
              onManageStreams={() => {
                studentTabRef.current?.switchPlayerSelector();
              }}
            />
          }
        >
          <PedagoSupervisorTabs.Tabs.StudentView>
            <PedagogyStudentDashboardContainer
              ref={studentTabRef}
              meetingContainerAPI={meetingContainerAPI}
              populatedMeetingData={populatedMeetingData}
              meeting={meeting}
              context={pedagogyCtx}
              layoutType={layoutType}
              audioValidator={studentAudioValidator}
            />
          </PedagoSupervisorTabs.Tabs.StudentView>
          <PedagoSupervisorTabs.Tabs.ExpertView badge={connectedStudentSessions.length > 0 ? `${connectedStudentSessions.length}` : undefined}>
            <PedagogyExpertDashboardContainer
              meetingContainerAPI={meetingContainerAPI}
              populatedMeetingData={populatedMeetingData}
              meeting={meeting}
              context={pedagogyCtx}
              onClick={(session) => setUserPopupData({ mode: "session", id: session?.id, sessionIndex: 0 })}
              audioValidator={expertAudioValidator}
              ongoingGroups={ongoingGroups.length ? true : false}
            />
          </PedagoSupervisorTabs.Tabs.ExpertView>
          <PedagoSupervisorTabs.Tabs.OnTheBoadView badge={highlightView ? " " : undefined}>
            <PedagogyHighlightContainer
              meeting={meeting}
              ongoingGroups={ongoingGroups?.length ? true : false}
              meetingContainerAPI={meetingContainerAPI}
              context={pedagogyCtx}
              populatedMeetingData={populatedMeetingData}
              highlightView={highlightView}
              layoutType={layoutType}
              showSelfVideo={false}
              audioValidator={highlightAudioValidator}
            />
          </PedagoSupervisorTabs.Tabs.OnTheBoadView>
          <PedagoSupervisorTabs.Tabs.GroupsView badge={ongoingGroups.length ? `${ongoingGroups.length}` : undefined}>
            <PedagogyGroupContainer
              style={{ zoom: 0.8 }}
              meetingContainerAPI={meetingContainerAPI}
              populatedMeetingData={populatedMeetingData}
              meeting={meeting}
              context={pedagogyCtx}
              currentExpertView={currentExpertView}
              audioValidator={groupAudioValidator}
              validators={{
                onJoinGroup: (group) => {
                  pageContextRef.current?.goTo(
                    PageMasterMode.MEETING_INTERVENTION_GROUP,
                    { id: pageContextRef.current.params?.id },
                    { queries: { groupId: group.id, action: MeetingPedagogyInterventionGroupPageQueryAction.JOIN } }
                  );
                  return false;
                },
                onStopSpying: (groupId) => {
                  pageContextRef.current?.goTo(
                    PageMasterMode.MEETING_INTERVENTION_GROUP,
                    { id: pageContextRef.current.params?.id },
                    { queries: { groupId, action: MeetingPedagogyInterventionGroupPageQueryAction.JOIN } }
                  );
                  return false;
                },
                onTalkToGroupLeader: (group) => {
                  pageContextRef.current?.goTo(
                    PageMasterMode.MEETING_INTERVENTION_GROUP,
                    { id: pageContextRef.current.params?.id },
                    { queries: { groupId: group.id, action: MeetingPedagogyInterventionGroupPageQueryAction.TALK_TO_LEADER } }
                  );
                  return false;
                },
                onTalkToGroupLeaderFromGroup: (group) => {
                  pageContextRef.current?.goTo(
                    PageMasterMode.MEETING_INTERVENTION_GROUP,
                    { id: pageContextRef.current.params?.id },
                    { queries: { groupId: group.id, action: MeetingPedagogyInterventionGroupPageQueryAction.TALK_TO_LEADER } }
                  );
                  return false;
                },
                onTalkToAllGroupLeaders: () => {
                  pageContextRef.current?.goTo(
                    PageMasterMode.MEETING_INTERVENTION_GROUP,
                    { id: pageContextRef.current.params?.id },
                    { queries: { action: MeetingPedagogyInterventionGroupPageQueryAction.TALK_TO_ALL_LEADERS } }
                  );
                  return false;
                },
                onTalkToAllGroupUsers: () => {
                  pageContextRef.current?.goTo(
                    PageMasterMode.MEETING_INTERVENTION_GROUP,
                    { id: pageContextRef.current.params?.id },
                    { queries: { action: MeetingPedagogyInterventionGroupPageQueryAction.TALK_TO_EVERYBODY } }
                  );
                  return false;
                },
              }}
            />
          </PedagoSupervisorTabs.Tabs.GroupsView>
          <PedagoSupervisorTabs.Tabs.SurveyView badge={currentSurvey ? " " : undefined}>
            <PedagogySurveyContainer
              meetingContainerAPI={meetingContainerAPI}
              context={pedagogyCtx}
              audioValidator={surveyAudioValidator}
              style={{ zoom: 0.8 }}
            />
          </PedagoSupervisorTabs.Tabs.SurveyView>
        </PedagoSupervisorTabs.Container>
      </div>
    );
  };

  const renderRightPanel = () => {
    const chatroom = meeting?.chatrooms?.[0];
    if (!chatroom) return null;
    return (
      <div className={styles.right}>
        <CallDataPedagogy
          entryCall={meeting?.callState === MeetingCallState.OPEN_START ? "in progress" : meeting?.callWasDone ? "done" : "pending"}
          endCall={meeting?.callState === MeetingCallState.OPEN_END ? "in progress" : meeting?.endCallWasDone ? "done" : "pending"}
          style={{ width: "100%" }}
        />
        <DashboardChat
          chatroom={chatroom}
          className={`${styles.chatContainer} ${styles.blockContainer}`}
          meetingContainer={meetingContainerAPI}
          onClick={(msg) => {
            const session = meetingRef.current?.sessions?.find((s) => s.user?.id === msg.user.id);
            if (!session) return;
          }}
          disableDeletion={false}
          disableReactions={false}
        />
        {/*<SoundSlider
          onVolumeChange={(e) => {
            const volume = Number(e.target.value);
            // set volume
          }}
          onMuteVolume={() => {
            // set 0
          }}
          onMaximumVolume={() => {
            // set 100
          }}
          onFocus={() => {}}
          isDark={false}
          volume={"50"}
          style={{ width: "100%" }}
        />*/}
      </div>
    );
  };

  const renderSessionArrows = () => {
    return (
      <RightLeftArrows
        onClick={(direction) => {
          if (!userPopupData) return;
          const sessions = getSessions(userPopupData);
          if (sessions.length <= 1) return;
          const change = direction === "left" ? -1 : 1;
          const nextIndex = (userPopupData.sessionIndex + change + sessions.length) % sessions.length;
          setUserPopupData({ ...userPopupData, sessionIndex: nextIndex });
        }}
      />
    );
  };

  const renderUserPopup = () => {
    if (!userPopupData || !meeting) return;

    const sessions = getSessions(userPopupData);
    if (!sessions.length) {
      setUserPopupData(undefined);
      return null;
    }
    const selectedSessionIdx = Math.min(userPopupData.sessionIndex, sessions.length - 1);
    const selectedSession = sessions[selectedSessionIdx];
    if (!selectedSession) {
      setUserPopupData(undefined);
      return null;
    }

    const user = selectedSession?.user;

    const highlighting = selectedSession.highlighting === MeetingSessionHighlightingState.ONGOING;
    const isMicrophoneActive = selectedSession.medias?.[0]?.audio.enabled ?? false;
    const superUser = isSuperUser(sessionRef.current);
    const moderator = sessionRef.current ? isMeetingSessionRoleGreaterThanOrEqual(sessionRef.current?.role, MeetingSessionRole.MODERATOR) : false;

    const currPedaUser = pedaUsers.find((u) => u.id === user?.id);
    const inWaitingRoom = currPedaUser?.message ? true : false;

    const currUser = inWaitingRoom ? currPedaUser : user;
    const displayExclusionButton = true;
    const disaplayGiveAWarningButton = true;
    const displayAllowEntryIntoClassButton = inWaitingRoom;
    const displayHighlightButton = !inWaitingRoom && !highlighting && moderator;
    const displayEnterInCommunicationButton = true;
    const displayNotifyButton = true;

    const executePedagogyAction = (type: EscaetPedagogyAction["type"], user?: MeetingPedagogyUserData | User) => {
      if (!user) return;
      const action = pedagogyCtxRef.current?.actions.find((a) => a.type === type);
      if (!action) return;
      action.onExecute([{ meetingId: meeting.id, ...user } as any], PedagogyActionOrigin.Other);
    };

    return (
      <ScreenPopup
        isMicrophoneActive={isMicrophoneActive}
        onToggleMicrophone={
          superUser
            ? async () => {
                if (isMicrophoneActive) meetingContainerAPI?.mute({ sessions: [{ id: selectedSession.id }] });
                else meetingContainerAPI?.unmute({ sessions: [{ id: selectedSession.id }] });
              }
            : undefined
        }
        onExclude={
          displayExclusionButton
            ? () => {
                executePedagogyAction("exclude", currUser);
                setUserPopupData(undefined);
              }
            : undefined
        }
        onWriteWarning={disaplayGiveAWarningButton ? () => executePedagogyAction("warn", currUser) : undefined}
        onAuthorize={displayAllowEntryIntoClassButton ? () => executePedagogyAction("tolerate", currUser) : undefined}
        onEnterInCommunication={displayEnterInCommunicationButton ? () => executePedagogyAction("contact", currUser) : undefined}
        onNotify={
          displayNotifyButton
            ? async () => {
                executePedagogyAction("notify", currUser);
                return false;
              }
            : undefined
        }
        onBringUpBoard={
          displayHighlightButton
            ? async () => {
                try {
                  startSessionHighlight(selectedSession.id);
                } catch (error) {
                  return false;
                }
                return true;
              }
            : undefined
        }
        onClose={() => {
          setUserPopupData(undefined);
        }}
      >
        <MeetingPlayerStyleContainer mode="dashboard" meeting={meeting} session={selectedSession} style={{ width: "100%", height: "60vh" }}>
          <MeetingSessionPlayer
            style={{ width: "100%", height: "100%" }}
            meeting={meeting}
            sessionId={selectedSession.id}
            ovenMediaSessions={receptionSessions}
            muted={true}
          />
          {sessions.length > 1 && renderSessionArrows()}
        </MeetingPlayerStyleContainer>
      </ScreenPopup>
    );
  };

  const userProfileData = useMemo(() => {
    return {
      firstName: selfUser?.identity?.firstname ?? "",
      lastName: selfUser?.identity?.lastname ?? "",
      profilePicture: selfUser?.identity?.avatar,
    };
  }, [selfUser]);
  return (
    <div className={`${styles.container} ${sos ? styles.sos : ""}`}>
      <PedagoSupervisorHeader
        className={styles.header}
        title={meeting?.room?.name}
        user={userProfileData}
        attendees={usersStats}
        onSearchUser={() => {
          setDisplaySearchUserPopup(true);
        }}
        onExit={() => {
          pageContextRef.current?.goTo(PageMasterMode.DASHBOARD);
        }}
        sos={sos}
        onStopSos={() => {
          if (!meeting) return;
          const overlay = (
            <Popup
              style={{
                width: "fit-content",
                height: "fit-content",
                padding: "2em",
              }}
              Content={
                <div style={{ display: "flex", flexDirection: "column", gap: "24px", justifyContent: "center", alignItems: "center" }}>
                  <ReactIcons.PedagoSosHorizontal color="#f05e5e" width="200" height="60" />
                  <div style={{ fontSize: "2em", fontWeight: 800, fontFamily: "Nunito, sans-serif" }}>Voulez-vous annuler l'appel du service pédagogique ?</div>
                  <div style={{ display: "flex", flexDirection: "row", gap: "20px", width: "100%" }}>
                    <ButtonFinal
                      text="Non"
                      textColor="rgb(130, 129, 127)"
                      background="rgb(241, 239, 235)"
                      cx="!px-[15px] !py-[10px] !font-[800] !rounded-[5px] !h-[72px] !text-[24px] !w-[580px]"
                      onClick={() => {
                        removePopup(overlay);
                      }}
                    />
                    <ButtonFinal
                      text="Oui"
                      textColor="#fff"
                      background="rgb(0, 144, 255)"
                      cx="!px-[15px] !py-[10px] !font-[800] !rounded-[5px] !h-[72px] !text-[24px] !w-[580px]"
                      onClick={() => {
                        userDataInSosMode.forEach((u) => {
                          updateUserData(meeting.id, { mode: "by_user_data_id", id: u.id }, { sosMode: false });
                        });
                        removePopup(overlay);
                      }}
                    />
                  </div>
                </div>
              }
            />
          );
          addPopup(overlay, "meeting-hangup-popup", "main");
        }}
      />
      <div className={styles.content}>
        {renderLeftPanel()}
        {renderContent()}
        {renderRightPanel()}
        {displaySearchUserPopup && (
          <PedagogyUtils.UserSearch
            users={usersCallData}
            onUserProfile={(u: UserCallData) => seeUserProfile(u)}
            onClose={() => setDisplaySearchUserPopup(false)}
          />
        )}
      </div>
      {renderUserPopup()}
      {pedaMeetingId && inWaitingRoom ? (
        <RoomViewer
          pedagogyRoom={undefined}
          pedagogyUsers={pedaUsers} // ? withMessage maybe?
          actions={roomViewerPedagogyActions}
          onClose={() => {
            setInWaitingRoom(false);
          }}
        />
      ) : null}
      <PedagogyMessageForm
        actions={pedagogyCtx?.actions}
        confirmableAction={pedagogyCtx?.confirmableAction}
        onClose={useCallback(() => {
          pedagogyCtxRef.current?.setConfirmableAction(undefined);
        }, [])}
      />
      {displayPopupSOS && (
        <Overlay>
          <PedagogySOSOverlay style={{ width: "100vw", height: "100vh", background: "#44444444" }} onClick={() => setDisplayPopupSOS(false)} />
        </Overlay>
      )}
      {meeting?.state === MeetingState.PAUSED ? (
        <PauseView
          mode="pedagogy"
          meeting={meeting}
          back={{
            text: "Sortir de la salle",
            onClick: () => {
              pageContextRef.current?.goTo(PageMasterMode.DASHBOARD);
            },
          }}
          onResume={async () => {
            await updateMeetingState(MeetingState.RUNNING);
          }}
          onStop={async () => {
            await stop();
          }}
        />
      ) : null}
    </div>
  );
};
